import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Download, ShieldCheck } from "lucide-react";
import { InsuranceApplication, Order } from "../../../../../services";
import sabrInsuranceLogo from "../../../../../assets/images/sabrinsurance.png";
import ClockIcon2 from "../../../../shared/icons/ClockIcon2";
import InsuranceApplicationModal from "./InsuranceApplicationModal";
import ArrangeInsuranceModal from "./ArrangeInsuranceModal";
import style from "./InsuranceBlock.module.scss";

const excludedApplicationStatuses = new Set([
  "awaitingoffer",
  "assigned",
  "completedbyexecutor",
  "completed",
]);
const insuranceOrderStatuses = new Set(["assignedexecutor", "confirmed"]);
const matchesOrder = (record, orderId) =>
  record?.orderId != null && String(record.orderId) === String(orderId);
const safePolicyUrl = (url) => (typeof url === "string" && /^https?:\/\//i.test(url) ? url : null);
const getLatestApplication = (application, submittedApplication) => {
  if (!submittedApplication) return application;
  if (
    !application ||
    application.id !== submittedApplication.id ||
    !matchesOrder(application, submittedApplication.orderId)
  ) {
    return submittedApplication;
  }
  const receivedAt = Date.parse(application.updatedAt);
  const submittedAt = Date.parse(submittedApplication.updatedAt);
  if (Number.isFinite(submittedAt) && (!Number.isFinite(receivedAt) || receivedAt < submittedAt)) {
    return submittedApplication;
  }
  return application;
};
const getOptionalRecord = async (request) => {
  try {
    const { data } = await request();
    return data || null;
  } catch (error) {
    if (error?.httpStatus === 404) return null;
    throw error;
  }
};

function OrderInsurance({ order, insuranceType, refreshDocuments }) {
  const { t } = useTranslation();
  const [application, setApplication] = useState(null);
  const [submittedApplication, setSubmittedApplication] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [modal, setModal] = useState(null);
  const [issued, setIssued] = useState(false);
  const generalAgreement = insuranceType === "GeneralAgreement";
  const status = (order.status || "").toLowerCase();

  useEffect(() => {
    let current = true;
    setLoaded(false);
    setLoadError(false);
    const load = async () => {
      try {
        const [nextApplication, nextInsurance] = await Promise.all([
          !generalAgreement && !order.insuranceIncluded
            ? getOptionalRecord(InsuranceApplication.get)
            : Promise.resolve(null),
          generalAgreement || order.insuranceIncluded
            ? getOptionalRecord(() => Order.getSabrInsuranceStatus(order.id, "expeditor"))
            : Promise.resolve(null),
        ]);
        if (!current) return;
        setApplication(nextApplication);
        setInsurance(matchesOrder(nextInsurance, order.id) ? nextInsurance : null);
        setLoaded(true);
      } catch {
        if (current) setLoadError(true);
      }
    };
    load();
    return () => {
      current = false;
    };
  }, [order.id, order.status, order.insuranceIncluded, generalAgreement, attempt]);

  const reload = () => setAttempt((value) => value + 1);
  const card = (title, description, icon, action, appearance = "") => (
    <div className={`${style.card} ${style[appearance] || ""}`}>
      <div className={style.icon}>{icon}</div>
      <div className={style.copy}>
        <p className={style.title}>{title}</p>
        {description && <p className={style.subtitle}>{description}</p>}
      </div>
      {action}
    </div>
  );
  const retryButton = (
    <button type="button" className={style.action} onClick={reload}>
      {t("common.retry")}
    </button>
  );

  if (!loaded || loadError) return null;

  if (insurance?.status === "Active") {
    const policyUrl = safePolicyUrl(insurance.policyUrl);
    return card(
      t("insuranceStatus.cargoInsured"),
      null,
      <ShieldCheck size={32} />,
      policyUrl && (
        <a className={style.action} href={policyUrl} target="_blank" rel="noopener noreferrer">
          <Download size={18} />
          {t("insuranceStatus.downloadPolicy")}
        </a>
      ),
    );
  }
  if (insurance || issued) {
    return card(
      t("expeditorInsurance.insuranceStatusTitle"),
      t("expeditorInsurance.insuranceStatusDescription"),
      <ClockIcon2 width={32} height={32} aria-hidden="true" />,
      retryButton,
      "pending",
    );
  }

  const currentApplication = getLatestApplication(application, submittedApplication);
  if (currentApplication?.status === "Rejected") return null;
  if (
    !generalAgreement &&
    currentApplication?.orderId != null &&
    !matchesOrder(currentApplication, order.id)
  )
    return null;
  if (!generalAgreement && matchesOrder(currentApplication, order.id)) {
    const pending = currentApplication.status === "Pending" || currentApplication.status === 0;
    return card(
      t(pending ? "expeditorInsurance.applicationPending" : "expeditorInsurance.applicationSubmitted"),
      t(
        pending
          ? "expeditorInsurance.applicationPendingDescription"
          : "expeditorInsurance.applicationStatusDescription",
      ),
      <ClockIcon2 width={32} height={32} aria-hidden="true" />,
      null,
      "pending",
    );
  }

  const canApply =
    !generalAgreement && order.insuranceIncluded === false && !excludedApplicationStatuses.has(status);
  const canInsure = generalAgreement && !order.insuranceIncluded && insuranceOrderStatuses.has(status);
  if (!canApply && !canInsure) return null;

  return (
    <>
      {card(
        t(canInsure ? "expeditorInsurance.insureTitle" : "expeditorInsurance.applyTitle"),
        t(canInsure ? "expeditorInsurance.insureDescription" : "expeditorInsurance.applyDescription"),
        <img src={sabrInsuranceLogo} width={120} height={92} alt="" />,
        (
          <button
            type="button"
            className={style.action}
            onClick={() => setModal(canInsure ? "insurance" : "application")}
          >
            {t(canInsure ? "expeditorInsurance.insure" : "expeditorInsurance.apply")}
          </button>
        ),
        "offer",
      )}
      {modal === "application" && (
        <InsuranceApplicationModal
          orderId={order.id}
          onClose={() => setModal(null)}
          onSubmitted={(data) => {
            setSubmittedApplication(data);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "insurance" && (
        <ArrangeInsuranceModal
          orderId={order.id}
          onClose={() => setModal(null)}
          onInvoiceUploaded={refreshDocuments}
          onSubmitted={() => {
            setIssued(true);
            setModal(null);
            reload();
          }}
        />
      )}
    </>
  );
}

export default function InsuranceBlock({ order, refreshDocuments }) {
  const company = useSelector((state) => state.user.company);
  const insuranceType = company?.insuranceType;
  if (
    !order?.id ||
    order.transportationType !== "Autotransport" ||
    !company ||
    ![null, "", "GeneralAgreement"].includes(insuranceType)
  )
    return null;
  return (
    <OrderInsurance
      key={`${company.id}:${order.id}:${insuranceType}`}
      order={order}
      insuranceType={insuranceType}
      refreshDocuments={refreshDocuments}
    />
  );
}
